import React, { useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';
import { Sparkles, Play, Pause, X, Plus, Volume2, VolumeX, Leaf, CheckCircle2, Trees, ChevronDown, ChevronUp } from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PRESETS = [
  { label: '10 min', minutes: 10 },
  { label: '25 min', minutes: 25 },
  { label: '45 min', minutes: 45 },
  { label: '60 min', minutes: 60 },
];

// First 14 days we auto-play the reassure voice — after that it's opt-in
const VOICE_ONBOARDING_KEY = 'parkera_voice_until';
const initVoicePref = () => {
  try {
    let until = localStorage.getItem(VOICE_ONBOARDING_KEY);
    if (!until) {
      until = String(Date.now() + 14 * 24 * 60 * 60 * 1000);
      localStorage.setItem(VOICE_ONBOARDING_KEY, until);
    }
    return Date.now() < parseInt(until, 10);
  } catch { return true; }
};

// =============== START SCREEN ===============
function StartScreen({ onStart }) {
  const [focusText, setFocusText] = useState('');
  const [minutes, setMinutes] = useState(25);
  const [starting, setStarting] = useState(false);
  const inputRef = useRef(null);

  useEffect(() => { setTimeout(() => inputRef.current?.focus(), 200); }, []);

  const start = async () => {
    if (!focusText.trim()) {
      toast.info('Skriv vad du ska göra först 💛');
      inputRef.current?.focus();
      return;
    }
    setStarting(true);
    try {
      const r = await axios.post(`${API}/parkera/start`, {
        focus_text: focusText.trim(), minutes,
      }, { withCredentials: true });
      onStart(r.data?.session);
    } catch {
      toast.error('Kunde inte starta. Försök igen 💛');
    } finally {
      setStarting(false);
    }
  };

  return (
    <div className="space-y-5">
      <div className="text-center">
        <div className="text-5xl mb-2">🌳</div>
        <h2 className="text-2xl font-bold text-emerald-700">Parkera resten</h2>
        <p className="text-sm text-emerald-600/80 italic mt-1">
          Säg vad du ska göra — jag håller koll på allt annat under tiden
        </p>
      </div>

      <div>
        <label className="text-xs font-bold uppercase tracking-wide text-emerald-700/70 mb-1.5 block">
          En sak — bara EN
        </label>
        <input
          ref={inputRef}
          value={focusText}
          onChange={(e) => setFocusText(e.target.value)}
          onKeyDown={(e) => e.key === 'Enter' && start()}
          placeholder="t.ex. Läsa kapitel 3 · Sminka mig i lugn och ro · Skriva mail till X"
          data-testid="parkera-focus-input"
          className="w-full px-4 py-3 rounded-2xl border-2 border-emerald-200 bg-white/90 focus:border-emerald-500 outline-none text-base text-emerald-900 placeholder:text-emerald-400/60"
          maxLength={200}
        />
      </div>

      <div>
        <label className="text-xs font-bold uppercase tracking-wide text-emerald-700/70 mb-1.5 block">
          Hur länge?
        </label>
        <div className="grid grid-cols-4 gap-2">
          {PRESETS.map((p) => (
            <button
              key={p.label}
              onClick={() => setMinutes(p.minutes)}
              data-testid={`parkera-preset-${p.minutes}`}
              className={`py-2.5 rounded-xl text-sm font-bold transition ${
                minutes === p.minutes
                  ? 'bg-emerald-500 text-white shadow-md scale-105'
                  : 'bg-white text-emerald-700 hover:bg-emerald-100 border border-emerald-200'
              }`}
            >
              {p.label}
            </button>
          ))}
        </div>
      </div>

      <button
        onClick={start}
        disabled={starting}
        data-testid="parkera-start-btn"
        className="w-full py-4 rounded-2xl bg-gradient-to-r from-emerald-500 to-teal-500 hover:from-emerald-600 hover:to-teal-600 text-white font-bold text-base shadow-lg disabled:opacity-50 flex items-center justify-center gap-2"
      >
        <Play className="w-5 h-5 fill-white" />
        {starting ? 'Förbereder…' : 'Starta — jag tar resten 🌳'}
      </button>

      <p className="text-[11px] text-emerald-600/70 text-center italic px-4">
        Du behöver inte hålla något i huvudet just nu.
      </p>
    </div>
  );
}

// =============== FOCUS SCREEN ===============
function FocusScreen({ session, onFinish }) {
  const [now, setNow] = useState(Date.now());
  const [paused, setPaused] = useState(!!session.paused_at);
  const [showLot, setShowLot] = useState(false);
  const [voiceOn, setVoiceOn] = useState(initVoicePref());
  const [hasSpoken, setHasSpoken] = useState(false);
  const speakRef = useRef(null);

  const endsAtMs = new Date(session.ends_at).getTime();
  const totalMs = session.minutes * 60 * 1000;
  const remainingMs = Math.max(0, endsAtMs - now);
  const progressPct = Math.max(0, Math.min(100, 100 - (remainingMs / totalMs) * 100));

  useEffect(() => {
    if (paused) return;
    const t = setInterval(() => setNow(Date.now()), 500);
    return () => clearInterval(t);
  }, [paused]);

  // Speak the reassure line once on mount (if voice opt-in is active)
  useEffect(() => {
    if (hasSpoken || !voiceOn || !session.reassure) return;
    setHasSpoken(true);
    try {
      if (!('speechSynthesis' in window)) return;
      const u = new SpeechSynthesisUtterance(session.reassure);
      u.lang = 'sv-SE';
      u.rate = 0.92;
      u.pitch = 1.02;
      speakRef.current = u;
      window.speechSynthesis.speak(u);
    } catch { /* speechSynthesis unavailable */ }
    return () => { try { window.speechSynthesis.cancel(); } catch { /* ignore */ } };
  }, []);

  // Auto-finish when time runs out
  useEffect(() => {
    if (remainingMs <= 0 && !paused) {
      onFinish('time_up');
    }
  }, [remainingMs, paused, onFinish]);

  const speakAgain = () => {
    if (!session.reassure) return;
    try {
      window.speechSynthesis.cancel();
      const u = new SpeechSynthesisUtterance(session.reassure);
      u.lang = 'sv-SE'; u.rate = 0.92; u.pitch = 1.02;
      window.speechSynthesis.speak(u);
    } catch { /* ignore */ }
  };

  const togglePause = async () => {
    try {
      const r = await axios.post(`${API}/parkera/current/pause`, {}, { withCredentials: true });
      const s = r.data?.session;
      setPaused(!!s?.paused_at);
    } catch { /* ignore */ }
  };

  const addTime = async (mins) => {
    try {
      await axios.post(`${API}/parkera/current/extend`, { minutes: mins }, { withCredentials: true });
      toast.success(`+${mins} min 💛`);
      // Force tick
      setNow(Date.now());
      window.location.reload();  // simplest re-fetch
    } catch { /* ignore */ }
  };

  const finishNow = () => onFinish('manual');

  const mins = Math.floor(remainingMs / 60000);
  const secs = Math.floor((remainingMs % 60000) / 1000);
  const timeStr = `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;

  return (
    <div className="relative">
      {/* Top bar */}
      <div className="flex items-center justify-between mb-4">
        <button
          onClick={() => setVoiceOn((v) => !v)}
          title={voiceOn ? 'Ljud på' : 'Ljud av'}
          data-testid="parkera-voice-toggle"
          className="p-2 rounded-full bg-white/60 hover:bg-white/90 text-emerald-700"
        >
          {voiceOn ? <Volume2 className="w-4 h-4" /> : <VolumeX className="w-4 h-4" />}
        </button>
        <div className="text-xs text-emerald-700/60 font-semibold uppercase tracking-wide">
          {paused ? 'Pausad' : 'Fokus pågår'}
        </div>
        <button
          onClick={finishNow}
          data-testid="parkera-finish-early"
          className="text-xs text-emerald-700/60 hover:text-emerald-800 hover:underline"
        >
          Avsluta nu
        </button>
      </div>

      {/* Focus text */}
      <div className="text-center mb-6">
        <p className="text-xs uppercase tracking-wider text-emerald-600/60 mb-1">Du gör nu</p>
        <h1 className="text-2xl sm:text-3xl font-bold text-emerald-800" data-testid="parkera-focus-display">
          {session.focus_text}
        </h1>
      </div>

      {/* Timer */}
      <div className="relative max-w-[260px] mx-auto mb-6">
        <div className="aspect-square rounded-full bg-gradient-to-br from-emerald-100 via-teal-100 to-cyan-100 shadow-inner flex items-center justify-center relative overflow-hidden border-4 border-white">
          {/* progress ring */}
          <svg className="absolute inset-0 -rotate-90" viewBox="0 0 100 100">
            <circle cx="50" cy="50" r="46" fill="none" stroke="rgba(16,185,129,0.15)" strokeWidth="6" />
            <circle
              cx="50" cy="50" r="46" fill="none"
              stroke="url(#parkera-grad)"
              strokeWidth="6"
              strokeLinecap="round"
              strokeDasharray={`${2 * Math.PI * 46}`}
              strokeDashoffset={`${2 * Math.PI * 46 * (1 - progressPct / 100)}`}
              style={{ transition: 'stroke-dashoffset 0.5s linear' }}
            />
            <defs>
              <linearGradient id="parkera-grad" x1="0" x2="1" y1="0" y2="1">
                <stop offset="0%" stopColor="#10b981" />
                <stop offset="100%" stopColor="#0891b2" />
              </linearGradient>
            </defs>
          </svg>
          <div className="text-center z-10">
            <div className="text-5xl sm:text-6xl font-bold text-emerald-800 tabular-nums" data-testid="parkera-timer">{timeStr}</div>
            <div className="text-[11px] text-emerald-600/70 mt-1">av {session.minutes} min</div>
          </div>
        </div>
      </div>

      {/* Reassure line */}
      <div className="bg-white/70 backdrop-blur rounded-2xl p-4 mb-5 border border-emerald-200 shadow-sm cursor-pointer hover:bg-white/90 transition" onClick={speakAgain} title="Klicka för att höra igen">
        <div className="flex items-start gap-2">
          <Sparkles className="w-4 h-4 text-emerald-500 flex-shrink-0 mt-0.5" />
          <p className="text-sm text-emerald-800 leading-relaxed italic" data-testid="parkera-reassure">
            "{session.reassure}"
          </p>
        </div>
        <p className="text-[10px] text-emerald-500/70 mt-1.5 text-center">🐾 Klicka för att höra meningen igen</p>
      </div>

      {/* Controls */}
      <div className="flex gap-2 justify-center">
        <button
          onClick={togglePause}
          data-testid="parkera-pause-toggle"
          className="px-4 py-2.5 rounded-xl bg-white border-2 border-emerald-200 hover:bg-emerald-50 text-emerald-700 font-bold text-sm flex items-center gap-1.5"
        >
          {paused ? <><Play className="w-4 h-4" /> Fortsätt</> : <><Pause className="w-4 h-4" /> Pausa</>}
        </button>
        <button
          onClick={() => addTime(5)}
          data-testid="parkera-extend-5"
          className="px-4 py-2.5 rounded-xl bg-emerald-100 hover:bg-emerald-200 text-emerald-700 font-bold text-sm flex items-center gap-1.5"
        >
          <Plus className="w-4 h-4" /> 5 min
        </button>
      </div>

      {/* Parking-lot peek (collapsed by default) */}
      <div className="mt-6 bg-white/50 rounded-2xl border border-emerald-100 overflow-hidden">
        <button
          onClick={() => setShowLot((s) => !s)}
          data-testid="parkera-lot-toggle"
          className="w-full px-4 py-2.5 flex items-center justify-between text-xs font-bold text-emerald-700 hover:bg-emerald-50"
        >
          <span className="flex items-center gap-2">
            <Trees className="w-3.5 h-3.5" />
            Jag håller {session.parking_count || 0} saker åt dig
          </span>
          {showLot ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
        </button>
        {showLot && (
          <div className="px-3 py-2 space-y-1 max-h-48 overflow-y-auto" data-testid="parkera-lot-list">
            {(session.parking_lot || []).map((it, i) => (
              <div key={i} className="flex items-center gap-2 bg-white/80 rounded-lg px-2 py-1.5 text-xs">
                <span className="text-base">{it.emoji}</span>
                <span className="flex-1 text-gray-700 truncate">{it.title}</span>
                {it.scheduled_for && (
                  <span className="text-[10px] text-gray-400 whitespace-nowrap">
                    {(() => {
                      try {
                        const d = new Date(it.scheduled_for);
                        const today = new Date();
                        const sameDay = d.toDateString() === today.toDateString();
                        return sameDay
                          ? d.toLocaleTimeString('sv-SE', { hour: '2-digit', minute: '2-digit' })
                          : d.toLocaleDateString('sv-SE', { month: 'short', day: 'numeric' });
                      } catch { return ''; }
                    })()}
                  </span>
                )}
              </div>
            ))}
            {(!session.parking_lot || session.parking_lot.length === 0) && (
              <div className="text-xs text-emerald-600/60 italic px-2 py-3 text-center">
                Inget annat att hålla just nu — fokusera helt fritt 💛
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

// =============== FINISH SCREEN ===============
function FinishScreen({ session, freshLot, onClose, onAnother }) {
  const accomplished = session.accomplished !== false;
  return (
    <div className="space-y-5 text-center">
      <div className="text-5xl">{accomplished ? '✨' : '💛'}</div>
      <div>
        <h2 className="text-xl font-bold text-emerald-700">
          {accomplished ? 'Klart!' : 'Inget krav på prestation'}
        </h2>
        <p className="text-sm text-emerald-600/80 mt-1 italic px-4">
          Du höll fokuset på <strong>{session.focus_text}</strong>.
          Allt annat finns kvar — och har en plats.
        </p>
      </div>

      {/* Fresh lot — what's next */}
      <div className="bg-white/70 backdrop-blur rounded-2xl p-4 border border-emerald-200 text-left">
        <h3 className="text-xs font-bold uppercase tracking-wide text-emerald-700 mb-2 flex items-center gap-1.5">
          <Trees className="w-3.5 h-3.5" /> Nästa sak väntar
        </h3>
        <div className="space-y-1 max-h-56 overflow-y-auto" data-testid="parkera-finish-lot">
          {(freshLot || []).slice(0, 8).map((it, i) => (
            <div key={i} className="flex items-center gap-2 bg-gradient-to-r from-white to-emerald-50/40 rounded-lg px-2 py-1.5 text-xs">
              <span className="text-base">{it.emoji}</span>
              <span className="flex-1 text-gray-700 truncate">{it.title}</span>
              {it.scheduled_for && (
                <span className="text-[10px] text-gray-400 whitespace-nowrap">
                  {(() => {
                    try {
                      const d = new Date(it.scheduled_for);
                      const today = new Date();
                      const sameDay = d.toDateString() === today.toDateString();
                      return sameDay
                        ? d.toLocaleTimeString('sv-SE', { hour: '2-digit', minute: '2-digit' })
                        : d.toLocaleDateString('sv-SE', { month: 'short', day: 'numeric' });
                    } catch { return ''; }
                  })()}
                </span>
              )}
            </div>
          ))}
          {(!freshLot || freshLot.length === 0) && (
            <p className="text-xs text-emerald-600/60 italic text-center py-2">Inget brådskande just nu 🌿</p>
          )}
        </div>
      </div>

      <div className="flex gap-2">
        <button
          onClick={onAnother}
          data-testid="parkera-another"
          className="flex-1 py-3 rounded-xl bg-emerald-500 hover:bg-emerald-600 text-white font-bold text-sm flex items-center justify-center gap-1.5"
        >
          <Leaf className="w-4 h-4" /> Parkera en sak till
        </button>
        <button
          onClick={onClose}
          data-testid="parkera-close-after"
          className="flex-1 py-3 rounded-xl bg-white border-2 border-emerald-200 hover:bg-emerald-50 text-emerald-700 font-bold text-sm flex items-center justify-center gap-1.5"
        >
          <CheckCircle2 className="w-4 h-4" /> Stäng
        </button>
      </div>
    </div>
  );
}

// =============== MAIN MODAL ===============
export default function ParkeraRestenModal({ open, onClose }) {
  const [phase, setPhase] = useState('loading'); // loading | start | focus | finish
  const [session, setSession] = useState(null);
  const [freshLot, setFreshLot] = useState(null);

  const loadCurrent = useCallback(async () => {
    setPhase('loading');
    try {
      const r = await axios.get(`${API}/parkera/current`, { withCredentials: true });
      if (r.data?.session) {
        setSession(r.data.session);
        setPhase('focus');
      } else {
        setPhase('start');
      }
    } catch {
      setPhase('start');
    }
  }, []);

  useEffect(() => {
    if (!open) return;
    loadCurrent();
  }, [open, loadCurrent]);

  const onStart = (s) => { setSession(s); setPhase('focus'); };

  const onFinish = async (reason) => {
    try {
      const r = await axios.post(`${API}/parkera/current/finish`, {
        accomplished: reason !== 'cancel', note: '',
      }, { withCredentials: true });
      setSession(r.data?.session);
      setFreshLot(r.data?.fresh_parking_lot || []);
      setPhase('finish');
    } catch {
      setPhase('start');
    }
  };

  const onAnother = () => { setSession(null); setFreshLot(null); setPhase('start'); };

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-[290] bg-gradient-to-br from-emerald-900/40 via-teal-900/40 to-cyan-900/40 backdrop-blur-md flex items-center justify-center p-3 sm:p-6 overflow-y-auto" data-testid="parkera-modal">
      {/* Ambient floating leaves */}
      <div className="absolute inset-0 pointer-events-none overflow-hidden">
        {[...Array(8)].map((_, i) => (
          <div
            key={i}
            className="absolute text-2xl opacity-20 select-none"
            style={{
              left: `${(i * 13) % 100}%`,
              top: `${(i * 17) % 100}%`,
              animation: `parkera-float ${10 + (i % 4) * 2}s ease-in-out infinite`,
              animationDelay: `${i * 0.7}s`,
            }}
          >
            {['🌿', '🍃', '✨', '🌸'][i % 4]}
          </div>
        ))}
      </div>

      <div className="relative bg-gradient-to-br from-emerald-50 via-teal-50 to-cyan-50 rounded-3xl max-w-md w-full shadow-2xl border border-emerald-200 max-h-[92vh] overflow-y-auto">
        <div className="absolute top-2 right-2 z-10">
          <button
            onClick={onClose}
            data-testid="parkera-close"
            className="p-2 rounded-full hover:bg-white/60 text-emerald-700"
          >
            <X className="w-5 h-5" />
          </button>
        </div>
        <div className="p-5 sm:p-6">
          {phase === 'loading' && <div className="text-center text-emerald-500 py-12">…</div>}
          {phase === 'start' && <StartScreen onStart={onStart} />}
          {phase === 'focus' && session && <FocusScreen session={session} onFinish={onFinish} />}
          {phase === 'finish' && session && <FinishScreen session={session} freshLot={freshLot} onClose={onClose} onAnother={onAnother} />}
        </div>
      </div>
      <style>{`
        @keyframes parkera-float {
          0%, 100% { transform: translateY(0) rotate(0); }
          50% { transform: translateY(-20px) rotate(15deg); }
        }
      `}</style>
    </div>
  );
}
